import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, Phone, Mail, MapPin, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { SectionLabel } from "@/components/Motion";
import { BRAND, TIERS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = ["Morning (8am–12pm)", "Afternoon (12pm–4pm)", "Evening (4pm–8pm)", "Flexible / Any time"];

export default function Contact() {
  const location = useLocation();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    tier: location.state?.tier || "", project_details: "", preferred_time: "",
  });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.tier || !form.project_details) {
      toast.error("Please fill in your name, email, tier and project details.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consult/inquiries`, {
        ...form,
        preferred_date: date ? format(date, "PPP") : "",
      });
      setDone(true);
      toast.success("Request sent! Manny will be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + BRAND.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        label="Get In Touch"
        titleLines={["Book Your", "Consult"]}
        sub="Tell Manny about your project and pick a time that works. Every inquiry gets a personal reply."
      />

      <section className="mx-auto max-w-[1400px] px-5 pb-32 md:px-10">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr]">
          {/* Form */}
          <div className="rounded-2xl border border-white/10 bg-ink-700 p-6 md:p-10">
            {done ? (
              <motion.div
                data-testid="contact-success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-20 text-center"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-amber text-black">
                  <Check className="h-8 w-8" strokeWidth={3} />
                </span>
                <h3 className="mt-6 font-head text-4xl font-medium tracking-tight text-white">Request Sent</h3>
                <p className="mt-3 max-w-sm text-white/60">
                  Thanks, {form.name.split(" ")[0]}. A confirmation is on its way to your inbox — Manny will reach out to lock in your session.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs uppercase tracking-widest text-white/60">Full Name *</Label>
                    <Input id="name" data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="border-white/15 bg-ink-800 text-white placeholder:text-white/30" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-white/60">Phone</Label>
                    <Input id="phone" data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="(310) 555-0100" className="border-white/15 bg-ink-800 text-white placeholder:text-white/30" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest text-white/60">Email *</Label>
                  <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="border-white/15 bg-ink-800 text-white placeholder:text-white/30" />
                </div>

                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-white/60">Advisory Tier *</Label>
                    <Select value={form.tier} onValueChange={set("tier")}>
                      <SelectTrigger data-testid="select-tier" className="border-white/15 bg-ink-800 text-white">
                        <SelectValue placeholder="Select a tier" />
                      </SelectTrigger>
                      <SelectContent className="border-white/15 bg-ink-700 text-white">
                        {TIERS.map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`tier-option-${t.id.toLowerCase()}`}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-white/60">Preferred Time</Label>
                    <Select value={form.preferred_time} onValueChange={set("preferred_time")}>
                      <SelectTrigger data-testid="select-time" className="border-white/15 bg-ink-800 text-white">
                        <SelectValue placeholder="Any time" />
                      </SelectTrigger>
                      <SelectContent className="border-white/15 bg-ink-700 text-white">
                        {TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-white/60">Preferred Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="date-trigger"
                        className="w-full justify-start border-white/15 bg-ink-800 text-left font-normal text-white hover:bg-ink-600 hover:text-white"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-amber" />
                        {date ? format(date, "PPP") : <span className="text-white/40">Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto border-white/15 bg-ink-700 p-0" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="details" className="text-xs uppercase tracking-widest text-white/60">Project Details *</Label>
                  <Textarea id="details" data-testid="input-details" value={form.project_details} onChange={set("project_details")} rows={5} placeholder="Tell us about your property, the challenge, and what you're hoping to achieve..." className="resize-none border-white/15 bg-ink-800 text-white placeholder:text-white/30" />
                </div>

                <Button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="h-auto w-full rounded-full bg-amber py-4 text-sm font-medium uppercase tracking-[0.2em] text-black hover:bg-amber/90"
                >
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : "Send Request"}
                </Button>
              </form>
            )}
          </div>

          {/* Aside */}
          <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-white/10 bg-ink-700 p-8">
              <SectionLabel>Direct Line</SectionLabel>
              <div className="mt-6 space-y-5">
                <a href={BRAND.phoneHref} className="flex items-center gap-4 text-white transition-colors hover:text-amber">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber/15 text-amber"><Phone className="h-5 w-5" /></span>
                  <div><div className="text-xs uppercase tracking-widest text-white/40">Call</div><div className="font-semibold">{BRAND.phone}</div></div>
                </a>
                <a href={`mailto:${BRAND.email}`} className="flex items-center gap-4 text-white transition-colors hover:text-amber">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber/15 text-amber"><Mail className="h-5 w-5" /></span>
                  <div><div className="text-xs uppercase tracking-widest text-white/40">Email</div><div className="font-semibold">{BRAND.email}</div></div>
                </a>
                <div className="flex items-center gap-4 text-white">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber/15 text-amber"><MapPin className="h-5 w-5" /></span>
                  <div><div className="text-xs uppercase tracking-widest text-white/40">Visit</div><div className="text-sm font-semibold">{BRAND.address}</div></div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-amber/40 bg-amber p-8 text-black">
              <div className="font-head text-3xl font-medium leading-none tracking-tight">Talk to Manny</div>
              <p className="mt-3 text-sm text-black/70">Prefer to talk it through first? Skip the form and reach Manny directly — no obligation, just straight, expert answers.</p>
              <div className="mt-4 text-xs font-medium tracking-widest text-black/60">License #{BRAND.license} · Los Angeles, CA</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
